import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { HiOutlineChevronDown, HiOutlineChevronUp } from 'react-icons/hi'
import type { IconType } from 'react-icons'
import { cn } from '../../lib/utils'

export interface VerticalScrollNavigatorProps {
  children: React.ReactNode
  /** Classe aplicada ao contêiner externo (wrapper). */
  className?: string
  /** Classe adicional aplicada à área rolável. */
  contentClassName?: string
  /** Classe extra para os botões de navegação. */
  buttonClassName?: string
  /** Fração da altura visível usada a cada clique. */
  stepRatio?: number
  /** Ícones customizados para cima/baixo. */
  iconUp?: IconType
  iconDown?: IconType
  /** Quando true, oculta os botões ao não haver rolagem disponível. */
  hideButtonsWhenDisabled?: boolean
  /** Remove o ajuste de padding/margem que oculta a barra de rolagem. */
  showScrollbar?: boolean
}

export interface VerticalScrollNavigatorHandle {
  scrollToTop: () => void
  scrollToBottom: () => void
  scrollBy: (pixels: number) => void
}

const DEFAULT_STEP_RATIO = 0.6

export const VerticalScrollNavigator = forwardRef<
  VerticalScrollNavigatorHandle,
  VerticalScrollNavigatorProps
>(
  (
    {
      children,
      className,
      contentClassName,
      buttonClassName,
      stepRatio = DEFAULT_STEP_RATIO,
      iconUp: IconUp = HiOutlineChevronUp,
      iconDown: IconDown = HiOutlineChevronDown,
      hideButtonsWhenDisabled = true,
      showScrollbar = false,
    },
    ref,
  ) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [canScrollUp, setCanScrollUp] = useState(false)
    const [canScrollDown, setCanScrollDown] = useState(false)

    const sanitizedStepRatio = useMemo(() => {
      if (!Number.isFinite(stepRatio) || stepRatio <= 0) {
        return DEFAULT_STEP_RATIO
      }
      if (stepRatio > 1) {
        return 1
      }
      return stepRatio
    }, [stepRatio])

    const updateScrollState = useCallback(() => {
      const element = scrollRef.current

      if (!element) {
        setCanScrollUp(false)
        setCanScrollDown(false)
        return
      }

      const maxScroll = element.scrollHeight - element.clientHeight
      const tolerance = 2

      if (maxScroll <= tolerance) {
        setCanScrollUp(false)
        setCanScrollDown(false)
        return
      }

      setCanScrollUp(element.scrollTop > tolerance)
      setCanScrollDown(element.scrollTop < maxScroll - tolerance)
    }, [])

    const scrollBy = useCallback(
      (direction: 'up' | 'down') => {
        const element = scrollRef.current

        if (!element) {
          return
        }

        const amount = element.clientHeight * sanitizedStepRatio

        element.scrollBy({
          top: direction === 'down' ? amount : -amount,
          behavior: 'smooth',
        })
      },
      [sanitizedStepRatio],
    )

    const scrollToExtreme = useCallback((position: 'top' | 'bottom') => {
      const element = scrollRef.current

      if (!element) {
        return
      }

      element.scrollTo({
        top: position === 'top' ? 0 : element.scrollHeight,
        behavior: 'smooth',
      })
    }, [])

    useImperativeHandle(
      ref,
      () => ({
        scrollToTop: () => scrollToExtreme('top'),
        scrollToBottom: () => scrollToExtreme('bottom'),
        scrollBy: pixels => {
          const element = scrollRef.current
          if (!element) {
            return
          }
          element.scrollBy({ top: pixels, behavior: 'smooth' })
        },
      }),
      [scrollToExtreme],
    )

    useEffect(() => {
      const element = scrollRef.current

      if (!element) {
        return
      }

      const handleScroll = () => updateScrollState()
      
      // Atualizar estado inicial após um pequeno delay
      const initialTimer = setTimeout(updateScrollState, 100)
      
      element.addEventListener('scroll', handleScroll, { passive: true })
      window.addEventListener('resize', updateScrollState)

      let resizeObserver: ResizeObserver | undefined
      let mutationObserver: MutationObserver | undefined

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          setTimeout(updateScrollState, 50)
        })
        resizeObserver.observe(element)
        Array.from(element.children).forEach(child => {
          resizeObserver?.observe(child as Element)
        })
      }

      if (typeof MutationObserver !== 'undefined') {
        mutationObserver = new MutationObserver(() => {
          if (resizeObserver) {
            Array.from(element.children).forEach(child => {
              resizeObserver.observe(child as Element)
            })
          }
          setTimeout(updateScrollState, 50)
        })

        mutationObserver.observe(element, {
          childList: true,
          subtree: true,
        })
      }

      return () => {
        clearTimeout(initialTimer)
        element.removeEventListener('scroll', handleScroll)
        window.removeEventListener('resize', updateScrollState)
        resizeObserver?.disconnect()
        mutationObserver?.disconnect()
      }
    }, [updateScrollState])

    const baseButtonClasses =
      'p-2 rounded-full border transition-colors shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary'

    const enabledButtonClasses =
      'bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-700'

    const disabledButtonClasses =
      'bg-gray-100/80 dark:bg-neutral-800/70 border-transparent opacity-40 cursor-not-allowed'

    const shouldRenderUpButton = canScrollUp || !hideButtonsWhenDisabled
    const shouldRenderDownButton = canScrollDown || !hideButtonsWhenDisabled

    return (
      <div className={cn('relative h-full', className)}>
        <div
          ref={scrollRef}
          className={cn(
            'h-full overflow-y-auto scroll-smooth',
            !showScrollbar && 'pr-4 -mr-4',
            contentClassName,
          )}
        >
          {children}
        </div>

        {shouldRenderUpButton && (
          <button
            type='button'
            onClick={() => scrollBy('up')}
            disabled={!canScrollUp}
            aria-label='Deslocar conteúdo para cima'
            className={cn(
              'absolute right-2 top-2 z-10',
              baseButtonClasses,
              canScrollUp ? enabledButtonClasses : disabledButtonClasses,
              buttonClassName,
            )}
          >
            <IconUp className='h-4 w-4 text-gray-600 dark:text-gray-300' />
          </button>
        )}

        {shouldRenderDownButton && (
          <button
            type='button'
            onClick={() => scrollBy('down')}
            disabled={!canScrollDown}
            aria-label='Deslocar conteúdo para baixo'
            className={cn(
              'absolute right-2 bottom-2 z-10',
              baseButtonClasses,
              canScrollDown ? enabledButtonClasses : disabledButtonClasses,
              buttonClassName,
            )}
          >
            <IconDown className='h-4 w-4 text-gray-600 dark:text-gray-300' />
          </button>
        )}
      </div>
    )
  },
)

VerticalScrollNavigator.displayName = 'VerticalScrollNavigator'

export { HorizontalScrollNavigator } from './HorizontalScrollNavigator'
export default VerticalScrollNavigator
